import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CaretRight, ChalkboardTeacher, Clock, ClipboardText } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

const KIND_LABEL = { guru: "Guru", murid: "Murid" };

function statusBadge(status) {
  const map = {
    Hadir: "bg-green-100 text-green-700",
    Terlambat: "bg-amber-100 text-amber-700",
    Izin: "bg-sky-100 text-sky-700",
    Sakit: "bg-sky-100 text-sky-700",
    Alpha: "bg-red-100 text-red-600",
    "Tidak Hadir": "bg-red-100 text-red-600",
    Belum: "bg-stone-200 text-stone-500",
  };
  return map[status] || "bg-stone-200 text-stone-500";
}

export default function AdminAttendance() {
  const { kind, group, kelas } = useParams();
  const navigate = useNavigate();
  const isDetail = !!kelas;

  const [ov, setOv] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setErr(false);
    if (isDetail) api.get(`/admin/attendance-overview/${encodeURIComponent(kelas)}`).then((r) => setDetail(r.data)).catch(() => setErr(true));
    else api.get("/admin/attendance-overview").then((r) => setOv(r.data)).catch(() => setErr(true));
  }, [isDetail, kelas]);

  // ---- Sub-class breakdown view ----
  if (!isDetail) {
    const data = kind === "guru" ? ov?.teachers : ov?.students;
    const subs = data?.groups?.[group]?.subclasses || {};
    return (
      <div>
        <PageHeader title={`Kehadiran ${KIND_LABEL[kind]} • ${group}`} />
        {kind === "guru" && (
          <div className="px-5 pt-4">
            <button data-testid="open-laporan-guru" onClick={() => navigate("/kelola/laporan-guru")}
              className="w-full bg-teal-50 border border-teal-100 text-teal-700 rounded-2xl p-3.5 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform">
              <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center shrink-0"><ClipboardText size={20} weight="duotone" /></div>
              <span className="flex-1 text-left font-semibold text-sm">Laporan Kehadiran Guru</span>
              <CaretRight size={18} className="text-teal-300" />
            </button>
          </div>
        )}
        <div className="px-5 pt-4 space-y-2.5">
          {err && <p className="text-center text-red-500 text-sm bg-white rounded-2xl border border-red-100 py-3">Gagal memuat data. Coba lagi.</p>}
          {Object.entries(subs).map(([sub, c], i) => {
            const empty = c.total === 0;
            return (
            <motion.button key={sub} data-testid={`sub-${sub}`} disabled={empty} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => !empty && navigate(`/admin/kehadiran/${kind}/kelas/${encodeURIComponent(sub)}`)}
              className={`w-full bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center justify-between shadow-sm transition-transform ${empty ? "opacity-50" : "active:scale-[0.98]"}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <span className="font-heading font-bold text-primary text-sm">{sub.split("-")[1] || sub}</span>
                </div>
                <span className="font-heading font-semibold text-stone-800">{sub}</span>
              </div>
              <span className="flex items-center gap-2">
                <span className="text-lg font-bold text-primary">{c.hadir}<span className="text-stone-400 text-sm font-medium">/{c.total}</span></span>
                {!empty && <CaretRight size={18} className="text-stone-300" />}
              </span>
            </motion.button>
            );
          })}
          {Object.keys(subs).length === 0 && !err && <p className="text-center text-stone-400 mt-8">Memuat data...</p>}
        </div>
      </div>
    );
  }

  // ---- Sub-class detail view ----
  return (
    <div>
      <PageHeader title={`Kelas ${kelas}`} />
      <div className="px-5 pt-4 pb-8">
        {kind === "guru" && (
          <button data-testid="open-laporan-guru" onClick={() => navigate("/kelola/laporan-guru")}
            className="w-full bg-teal-50 border border-teal-100 text-teal-700 rounded-2xl p-3.5 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform mb-4">
            <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center shrink-0"><ClipboardText size={20} weight="duotone" /></div>
            <span className="flex-1 text-left font-semibold text-sm">Laporan Kehadiran Guru</span>
            <CaretRight size={18} className="text-teal-300" />
          </button>
        )}
        {err ? (
          <p className="text-center text-red-500 text-sm bg-white rounded-2xl border border-red-100 py-3 mt-4">Gagal memuat data. Coba lagi.</p>
        ) : !detail ? (
          <p className="text-center text-stone-400 mt-10">Memuat...</p>
        ) : (
          <>
            {/* Teachers */}
            <p className="font-heading font-semibold text-stone-800 mb-2 flex items-center gap-2">
              <ChalkboardTeacher size={20} weight="duotone" className="text-primary" /> Umi Guru
            </p>
            <div className="space-y-2 mb-6">
              {(detail.teachers || []).map((t) => (
                <button key={t.id} data-testid={`detail-teacher-${t.id}`} onClick={() => navigate(`/kelola/guru/${t.id}`)}
                  className="w-full bg-white rounded-2xl border border-stone-200/60 p-3 flex items-center gap-3 shadow-sm text-left active:scale-[0.98] transition-transform">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-primary text-sm font-bold">{t.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-800 text-sm truncate">{t.name}</p>
                    {t.time_in && <p className="text-[11px] text-stone-400 flex items-center gap-1"><Clock size={11} weight="fill" /> Masuk {t.time_in.replace(".", ":")}</p>}
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${statusBadge(t.status)}`}>{t.status}</span>
                  <CaretRight size={18} className="text-stone-300 shrink-0" />
                </button>
              ))}
              {(detail.teachers || []).length === 0 && <p className="text-stone-400 text-xs text-center py-2">Belum ada Umi Guru.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
